import { useEffect, useState } from 'react'

// useMediaQuery — subscribe a component to a CSS media query so the design
// system can adapt its layout (not just its CSS) across breakpoints.
//
// SSR/jsdom-safe: returns `false` until mounted and when `matchMedia` is
// unavailable, then tracks live changes. The breakpoints mirror tailwind.config
// (sm 640 / md 768 / lg 1024 / xl 1280).
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = (): void => setMatches(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/**
 * True below the `lg` (1024px) breakpoint — i.e. phone + tablet, where the app
 * shell collapses to an icon rail and dense layouts drop a panel. The default
 * (`false`) means "assume desktop" until proven otherwise, so server/first paint
 * matches the lg layout the 33 mockups are drawn at.
 */
export function useIsBelowLg(): boolean {
  return useMediaQuery('(max-width: 1023px)')
}

/** True in the tablet band only (768–1023px). */
export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
}

/**
 * True below the `md` (768px) breakpoint — i.e. phone, where dense tables drop
 * their columnar layout for a stacked card list. Defaults to `false` (desktop)
 * until mounted, matching the table layout the mockups are drawn at.
 */
export function useIsBelowMd(): boolean {
  return useMediaQuery('(max-width: 767px)')
}
